/**
 * Per-market branding and defaults.
 *
 * The product is sold under a different name in each market, and each market opens on a different
 * symbol, so both follow the customer's market rather than the deployment. Market ids are the ones
 * the backend's `Market` enum defines and that `user.marketId` carries: 1 = US/International,
 * 2 = India.
 *
 * Kept in one place because the watermark alone was set from three (the ECharts render, the admin
 * small-watermark toggle, and the chart theme default), and the default ticker from another.
 * Every extra copy is another chance to miss one when a market is added.
 */

/**
 * Watermark text by market id.
 *
 * <h2>⚠ BOTH MARKETS ARE `Dhanabhyas` NOW — the product took the India brand</h2>
 *
 * Market 1 was `integralDelta.ai`, and the reasoning recorded with it was that a watermark should
 * carry the DOMAIN rather than a bare name, because it is a provenance mark on an image that gets
 * exported, pasted into a deck and screenshotted onward — by which point the chart is the only
 * thing left pointing back. That argument is sound and is NOT what changed; what changed is the
 * name. The bare form is used here because it is the name the product is now called, and inventing
 * a domain the deployment does not serve would be worse provenance than no domain at all.
 *
 * <p>Market 2 already said `Dhanabhyas`, so this is the two markets converging on one brand rather
 * than a second rebrand. The per-market TABLE stays, because the markets can diverge again and
 * collapsing it to a constant is the change that would make that unexpressible.
 *
 * <p>⚠ THIS IS KEYED BY MARKET, NOT BY PRODUCT, so it reaches every build — including an
 * `integraldelta` one. There is no product axis here to scope it to; adding one would make the
 * watermark disagree with the customer's own market, which is the thing it is for.
 */
export const WATERMARK_BY_MARKET_ID = {
  1: 'Dhanabhyas',
  2: 'Dhanabhyas',
};

/** Symbol each market opens on, matching Market.defaultSymbol in the backend. */
export const DEFAULT_TICKER_BY_MARKET_ID = {
  1: 'SPY',
  2: 'NSEI',
};

/**
 * Fallback when the market is unknown — an anonymous visitor, or the moment before the profile has
 * loaded. The US brand is the safe default: it is the deployment's own name, so showing it briefly
 * is not a mislabel the way showing the India brand to a US customer would be.
 */
export const DEFAULT_WATERMARK = WATERMARK_BY_MARKET_ID[1];

/** The market ids the product actually supports. */
export const KNOWN_MARKET_IDS = Object.keys(WATERMARK_BY_MARKET_ID).map(Number);

/**
 * Normalises whatever form of "market" is to hand into an id.
 *
 * Accepts the number, its string form, or the market code. `/api/user/me` returns BOTH
 * `marketId: 2` and `market: "IN"` — the latter is a derived view the backend serialises — so
 * taking either means a profile that somehow carries only one of them still resolves correctly.
 */
export function toMarketId(market) {
  if (market === null || market === undefined || market === '') return null;

  const s = String(market).trim();
  if (/^\d+$/.test(s)) {
    // Validated against the markets we know rather than returned as-is. An id of 99 is not a
    // market, and answering "99" would push the decision onto every caller — one of which would
    // eventually forget to check and brand a chart from an undefined lookup.
    const n = Number(s);
    return KNOWN_MARKET_IDS.includes(n) ? n : null;
  }

  const code = s.toUpperCase();
  if (code === 'IN' || code === 'IND' || code === 'INDIA') return 2;
  if (code === 'US' || code === 'INTERNATIONAL') return 1;
  return null;
}

/** Watermark text for a market id, code, or name. */
export function watermarkForMarket(market) {
  const id = toMarketId(market);
  return (id !== null && WATERMARK_BY_MARKET_ID[id]) || DEFAULT_WATERMARK;
}

/**
 * The symbol a market should open on, or null when the market is unknown.
 *
 * Null rather than a US default on purpose: the caller falls back to the first symbol of whichever
 * list actually loaded, which is always a valid choice, whereas insisting on SPY for an unknown
 * market would select a ticker that may not be in the customer's list at all.
 */
export function defaultTickerForMarket(market) {
  const id = toMarketId(market);
  return (id !== null && DEFAULT_TICKER_BY_MARKET_ID[id]) || null;
}

/**
 * THE WATERMARK, at 4%.
 *
 * <p>It was 40%, which on a 250px string across the middle of the plot is a second chart. At a
 * full-history zoom the branding was legible through every candle and competed with the one thing
 * the image exists to show. Four percent still reproduces in a screenshot and in print, and is
 * invisible against price action.
 *
 * <p>Named rather than inlined because the two theme blocks below and the LIVE watermark the chart
 * builds each carried their own copy of the number, and only one of the three is actually
 * rendered -- so the other two could drift without anyone noticing.
 */
export const WATERMARK_FILL = 'rgba(180,180,180,0.04)';

/**
 * THE WATERMARK FILL FOR A GIVEN CANVAS — because one grey cannot serve two.
 *
 * <h2>⚠ `WATERMARK_FILL` IS INVISIBLE ON WHITE, and the arithmetic is the whole report</h2>
 *
 * `rgba(180,180,180,0.04)` composited over `#ffffff` is `255 - (255-180) x 0.04` = **#fcfcfc**:
 * three values off the page, a contrast ratio of **1.02 : 1**. That is not "faint", it is a mark
 * nobody can see — which is exactly how it was reported. The same colour over the dark canvas
 * `#0d1117` lands near `rgb(20,24,29)`, which is low too but reads, because alpha compositing
 * lifts further per unit of alpha in the shadows than it darkens in the highlights.
 *
 * <p>So the ONE value was tuned for the dark canvas and shipped on both.
 *
 * <h2>⚠ A DARKER INK ON LIGHT, NOT A HIGHER ALPHA ON THE SAME GREY</h2>
 *
 * Raising the alpha on `rgb(180,180,180)` is the obvious fix and it saturates: at 100% it is still
 * only 1.9:1 against white, because the ink itself is nearly as light as the page. The light
 * branch therefore uses a dark ink at a low alpha, which is the same mechanism the dark branch
 * already relies on in reverse.
 *
 * <p>⚠ AND IT STAYS A WATERMARK. The brief this originally shipped under is explicit — it was 40%
 * once, "a second chart ... legible through every candle", and was cut to 4% for that reason. The
 * light value is chosen to be the FAINTEST thing that survives a screenshot rather than the most
 * readable: measured over `#f9fafe`, `rgba(20,24,29,0.05)` is **1.19 : 1** against the canvas —
 * visible as a shape, still well under the ~3:1 at which it would start competing with price.
 *
 * <p>A test pins both directions, because a contrast assertion alone would pass just as happily
 * if the light value were applied to the dark canvas.
 *
 * @param mode `'dark'` or anything else, matching `theme.palette.mode`.
 */
export const watermarkFillFor = (mode) => (
  mode === 'dark' ? WATERMARK_FILL : 'rgba(20,24,29,0.05)'
);
